<script setup lang="ts">
/**
 * ErrorBoundary：捕获子组件渲染错误并显示降级 UI。
 *
 * 用法：
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * 或通过 :onError 监听：
 *   <ErrorBoundary :on-error="(err) => track(err)">
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
import { ref, onErrorCaptured } from 'vue'

interface Props {
  onError?: (error: Error) => void
}

const props = defineProps<Props>()

const error = ref<Error | null>(null)

onErrorCaptured((err: unknown) => {
  const e = err instanceof Error ? err : new Error(String(err))
  error.value = e
  props.onError?.(e)
  // 上报到 Faro（异步、不阻塞降级渲染）
  import('@/observability/faro').then(({ pushEvent }) => {
    pushEvent('vue_error_boundary', {
      message: e.message,
      stack: e.stack ?? '',
    })
  })
  // 阻止错误继续向上冒泡
  return false
})

const reset = () => {
  error.value = null
}
</script>

<template>
  <div v-if="error" class="error-boundary" role="alert">
    <h2 class="text-[1.5rem]">⚠️ 出错了</h2>
    <p class="mt-2 text-[1rem]">{{ error.message }}</p>
    <button
      type="button"
      class="mt-4 px-4 py-2 rounded border border-[#40E0FF] text-[#40E0FF] hover:bg-[#40E0FF] hover:text-black transition"
      @click="reset"
    >
      重试
    </button>
  </div>
  <slot v-else />
</template>

<style scoped>
.error-boundary {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
  color: white;
  text-align: center;
}
</style>
